import React from 'react';
import { siteConfig } from '../lib/config';
import type { SessionState } from './useLivestreamSession';

export type SharedSessionStatus = 'idle' | 'connecting' | 'running' | 'failed';

const INITIAL_SESSION: SessionState = {
  sessionId: null,
  countdown: 0,
  timeBet: 35,
  endBettingTime: null,
  stopBetSecond: 3,
  status: '',
  coins: [],
  jackpot: 0,
  dealerName: null,
  odds: null,
  betSummary: null,
  totalPlayers: null,
  lastResult: null,
  betTypeResult: null,
  history: [],
};

const MAX_RETRY = 5;

// ─── Connection pool dùng chung ─────────────────────────────────────
//
// Server khoá session data theo (userId, gameType) — xem SessionDataCache —
// nên mọi component cùng xem một game nhận stream giống hệt nhau. Trước đây
// mỗi GroupCard tự mở 1 EventSource theo accountId, 6 group XD thành 6 stream
// trùng lặp: cạn 6 kết nối HTTP/1.1 của browser và tốn ~6× băng thông.
//
// Pool này giữ đúng 1 EventSource cho mỗi gameType, đếm ref để đóng khi
// component cuối cùng unmount, và phát lại state gần nhất cho subscriber mới
// vào để họ không phải chờ tới tick kế tiếp.

interface Channel {
  es: EventSource | null;
  refs: number;
  lastState: SessionState;
  status: SharedSessionStatus;
  retryCount: number;
  retryTimer: ReturnType<typeof setTimeout> | null;
  stateListeners: Set<(state: SessionState) => void>;
  statusListeners: Set<(status: SharedSessionStatus) => void>;
}

const channels = new Map<string, Channel>();

function setChannelStatus(channel: Channel, status: SharedSessionStatus): void {
  if (channel.status === status) return;
  channel.status = status;
  channel.statusListeners.forEach((fn) => fn(status));
}

function openChannel(gameType: string, channel: Channel): void {
  if (channel.retryTimer) {
    clearTimeout(channel.retryTimer);
    channel.retryTimer = null;
  }

  setChannelStatus(channel, 'connecting');

  const url = `${siteConfig.apiUrl}/api/v1/sunwin/livestream/session?gameType=${encodeURIComponent(gameType)}`;
  const es = new EventSource(url, { withCredentials: true });
  channel.es = es;

  es.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data) as SessionState;
      channel.lastState = data;
      channel.retryCount = 0;
      setChannelStatus(channel, 'running');
      channel.stateListeners.forEach((fn) => fn(data));
    } catch (err) {
      console.error('[useSharedSession] Failed to parse SSE data:', err);
    }
  };

  es.onerror = () => {
    es.close();
    channel.es = null;

    // Không còn ai xem thì thôi, đừng reconnect nền
    if (channel.refs === 0) return;

    const delay = Math.min(1000 * Math.pow(2, channel.retryCount), 10000);
    channel.retryCount += 1;

    // Sau MAX_RETRY lần liên tiếp, báo UI biết (chuyển 'failed') nhưng KHÔNG dừng
    // hẳn — vẫn còn ai đó xem thì cứ thử lại đều đặn mỗi 10s. Server có thể vẫn
    // đang start (TL bắt tay 2 lớp lâu hơn ngưỡng retry ban đầu) hoặc vừa restart;
    // dừng hẳn ở đây nghĩa là panel kẹt lỗi vĩnh viễn dù account sau đó chạy tốt.
    if (channel.retryCount > MAX_RETRY) {
      setChannelStatus(channel, 'failed');
    } else if (channel.status !== 'running') {
      // Giữ 'running' nếu đang chạy để UI không nháy khi reconnect nhanh
      setChannelStatus(channel, 'connecting');
    }

    channel.retryTimer = setTimeout(() => {
      channel.retryTimer = null;
      if (channel.refs > 0) openChannel(gameType, channel);
    }, delay);
  };
}

function closeChannel(gameType: string, channel: Channel): void {
  if (channel.retryTimer) {
    clearTimeout(channel.retryTimer);
    channel.retryTimer = null;
  }
  channel.es?.close();
  channel.es = null;
  channels.delete(gameType);
}

function subscribeChannel(
  gameType: string,
  onState: (state: SessionState) => void,
  onStatus: (status: SharedSessionStatus) => void,
): () => void {
  let channel = channels.get(gameType);

  if (!channel) {
    channel = {
      es: null,
      refs: 0,
      lastState: INITIAL_SESSION,
      status: 'idle',
      retryCount: 0,
      retryTimer: null,
      stateListeners: new Set(),
      statusListeners: new Set(),
    };
    channels.set(gameType, channel);
  }

  const active = channel;
  active.refs += 1;
  active.stateListeners.add(onState);
  active.statusListeners.add(onStatus);

  // Bơm ngay state đã có để component mới mount hiển thị được liền
  if (active.lastState !== INITIAL_SESSION) onState(active.lastState);
  onStatus(active.status);

  if (!active.es && !active.retryTimer) {
    active.retryCount = 0;
    openChannel(gameType, active);
  }

  let released = false;
  return () => {
    if (released) return;
    released = true;

    active.stateListeners.delete(onState);
    active.statusListeners.delete(onStatus);
    active.refs -= 1;

    if (active.refs <= 0) closeChannel(gameType, active);
  };
}

/**
 * useSharedSession — subscribe session data đã parse sẵn trên server qua SSE.
 *
 * Khác useLivestreamSession (parse raw log theo từng account), hook này đọc
 * sessionDataCache dùng chung theo game. Mọi component truyền cùng `gameType`
 * chia sẻ đúng một EventSource.
 *
 * @param gameType — 'TX' | 'TL' | 'XD'
 * @param enabled — có subscribe hay không
 */
export function useSharedSession(gameType: string | undefined, enabled: boolean = true) {
  const [session, setSession] = React.useState<SessionState>(INITIAL_SESSION);
  const [status, setStatus] = React.useState<SharedSessionStatus>('idle');

  React.useEffect(() => {
    if (!enabled || !gameType) {
      setStatus('idle');
      return;
    }
    return subscribeChannel(gameType, setSession, setStatus);
  }, [gameType, enabled]);

  return { session, status };
}
